'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useI18n } from '@/lib/i18n/context'
import { LabelIcon, BCorpIcon } from '@/components/icons'
import { requestReopenConsentBanner } from '@/lib/consent'
import { temoinSessionPresent } from '@/lib/auth-securite'
import { reductionsAttendues } from '@/lib/site/chiffres-calcul'

const certifications = [
  { name: 'Label Bas-Carbone', Icon: LabelIcon },
  { name: 'B Corp', Icon: BCorpIcon },
]

function NewsletterForm({ label, placeholder }: { label: string; placeholder: string }) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !email.includes('@')) return
    setStatus('loading')
    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      if (res.ok) {
        setStatus('done')
        setEmail('')
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  if (status === 'done') {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-300 font-medium">
        <svg aria-hidden="true" focusable="false" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        Inscription confirmée !
      </div>
    )
  }

  return (
    <form className="flex w-full md:w-auto gap-3" onSubmit={handleSubmit}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle') }}
        placeholder={placeholder}
        className="bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-amber focus:shadow-[0_0_0_3px_rgba(212,162,76,0.2)] w-full md:w-64 transition-all duration-300"
      />
      <button type="submit" disabled={status === 'loading'} className="btn-primary text-sm !py-2.5 whitespace-nowrap disabled:opacity-50">
        {status === 'loading' ? '...' : label}
      </button>
      {status === 'error' && <span className="text-red-300 text-xs self-center">Erreur</span>}
    </form>
  )
}

/** @param totalCO2  total des tonnes du portefeuille, calculé par le layout serveur : importer
 *   @/data/projects ici chargeait real-projects.json (1,5 Mo) sur toutes les pages (audit QUAL-17). */
export default function Footer({ totalCO2 }: { totalCO2: number }) {
  const { t, locale } = useI18n()
  // Un seul accès à l'espace (comme l'en-tête) : « Espace contributeur »
  // (présentation et connexion), ou « Mon espace » si le témoin non secret
  // de session est présent — lu dans le navigateur, sans requête.
  const [connecte, setConnecte] = useState(false)
  useEffect(() => {
    setConnecte(temoinSessionPresent(document.cookie))
  }, [])

  // Helper: prefix a FR path with /en when EN locale is active
  const localized = (path: string): string => {
    if (locale === 'en') {
      if (path === '/') return '/en'
      return `/en${path}`
    }
    return path
  }

  const footerLinks = {
    solutions: {
      title: t.footer.solutions,
      links: [
        { name: t.footer.solutionsLinks.entreprises, href: localized('/entreprises') },
        connecte
          // Lien simple (pas de préchargement de l'espace, page de session)
          ? { name: t.nav.myAccount, href: '/espace', simple: true }
          : { name: t.nav.contributorSpace, href: localized('/espace-contributeur') },
        { name: t.footer.solutionsLinks.pricing, href: localized('/pricing') },
        { name: t.footer.solutionsLinks.impact, href: localized('/impact') },
        { name: t.footer.solutionsLinks.reportingCsrd, href: localized('/reporting-csrd') },
        { name: t.footer.solutionsLinks.forestiers, href: localized('/forestiers') },
        { name: t.footer.solutionsLinks.agricoles, href: localized('/agricoles') },
      ],
    },
    company: {
      title: t.footer.company,
      links: [
        { name: t.footer.companyLinks.about, href: localized('/a-propos') },
        { name: t.footer.companyLinks.caseStudies, href: localized('/cas-clients') },
        { name: t.footer.companyLinks.engagements, href: localized('/a-propos#engagements') },
        { name: t.footer.companyLinks.blog, href: localized('/blog') },
        { name: t.footer.companyLinks.contact, href: localized('/contact') },
      ],
    },
    resources: {
      title: locale === 'en' ? 'Resources' : 'Ressources',
      links: [
        { name: locale === 'en' ? 'Resources' : 'Ressources', href: localized('/ressources') },
        { name: locale === 'en' ? 'Carbon Glossary' : 'Glossaire carbone', href: localized('/glossaire') },
        { name: locale === 'en' ? 'Submit a project' : 'Proposer un projet', href: localized('/proposer-projet') },
        { name: locale === 'en' ? 'Get a quote' : 'Demande de devis', href: localized('/demande-devis') },
        { name: 'FAQ', href: localized('/faq') },
      ],
    },
    legal: {
      title: t.footer.legal,
      links: [
        { name: t.footer.legalLinks.mentions, href: localized('/mentions-legales') },
        { name: t.footer.legalLinks.confidentialite, href: localized('/confidentialite') },
        { name: t.footer.legalLinks.cgu, href: localized('/cgu') },
        { name: t.footer.legalLinks.cookies, href: localized('/cookies') },
        { name: locale === 'en' ? 'Complaints' : 'Réclamations', href: localized('/reclamations') },
      ],
    },
  }

  // Arrondi : « plus de 600 000 » (src/lib/site/chiffres-calcul.ts)
  const formattedCO2 = reductionsAttendues(totalCO2, locale === 'en' ? 'en' : 'fr')

  return (
    <footer className="gradient-forest text-white">
      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="container-site py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-heading font-bold text-base text-white">{t.footer.newsletter}</h3>
            <p className="text-white/60 text-xs mt-0.5">{t.footer.newsletterSub}</p>
          </div>
          <NewsletterForm label={t.footer.subscribe} placeholder={t.footer.emailPlaceholder} />
        </div>
      </div>

      {/* Main footer — compact layout */}
      <div className="container-site py-8">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-6 lg:gap-8">
          {/* Brand column */}
          <div className="col-span-2">
            <Link href={localized('/')} className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-amber flex items-center justify-center text-forest font-heading font-bold text-sm">
                S
              </div>
              <span className="font-heading font-bold text-lg text-white">Stock CO2</span>
            </Link>
            <p className="text-white/50 text-xs leading-relaxed mb-4">{t.footer.tagline}</p>
            {/* B Corp badge */}
            <div className="flex items-center gap-2 mb-4 bg-white/5 border border-white/10 rounded-lg px-3 py-2 w-fit">
              <div className="w-8 h-8 rounded bg-white flex items-center justify-center flex-shrink-0">
                <span className="text-black font-heading font-extrabold text-sm">B</span>
              </div>
              <div>
                <p className="text-white text-xs font-semibold leading-tight">Certified B Corp</p>
                <p className="text-white/40 text-[10px]">{locale === 'en' ? 'Since 2024' : 'Depuis 2024'}</p>
              </div>
            </div>

            <div className="flex gap-2">
              {certifications.map((cert) => (
                <div
                  key={cert.name}
                  className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                  title={cert.name}
                >
                  <cert.Icon className="w-4 h-4" />
                </div>
              ))}
              <a
                href="https://www.linkedin.com/company/stockco2/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                title="LinkedIn"
              >
                <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Link columns — tighter spacing */}
          {Object.values(footerLinks).map((section) => (
            <div key={section.title}>
              <h4 className="font-heading font-semibold text-xs uppercase tracking-wider text-amber mb-3">
                {section.title}
              </h4>
              <ul className="space-y-1.5">
                {section.links.map((link) => (
                  <li key={link.name}>
                    {'simple' in link && link.simple ? (
                      <a href={link.href} className="text-white/60 text-xs hover:text-white transition-colors">
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        href={link.href}
                        className="text-white/60 text-xs hover:text-white transition-colors"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container-site py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-white/40 text-xs">
          <p>
            &copy; {new Date().getFullYear()} Stock CO2. {t.footer.rights}
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {/* Carbon weight badge */}
            <a
              href="https://www.websitecarbon.com/website/stock-co2-vercel-app/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1 hover:bg-white/10 transition-colors"
            >
              <svg aria-hidden="true" focusable="false" className="w-3 h-3 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              <span className="text-green-400 font-medium">0.09g CO₂</span>
              <span className="text-white/30">{locale === 'en' ? 'per page — eco-designed site' : 'par page — site éco-conçu'}</span>
            </a>
            <span className="hidden md:inline">•</span>
            <span>
              {formattedCO2} {t.stats.tco2}
            </span>
            <span className="hidden md:inline">•</span>
            <span className="hidden md:inline">{t.footer.certified}</span>
            <span className="hidden md:inline">•</span>
            {/* Rouvre le bandeau de consentement (référencé par /mentions-legales, /confidentialite, /cookies) */}
            <button
              type="button"
              onClick={requestReopenConsentBanner}
              className="text-white/30 hover:text-white/60 transition-colors"
            >
              {locale === 'en' ? 'Manage cookies' : 'Gérer mes cookies'}
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}
